import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	MenuItem,
	FormControl,
	Select,
	Button,
	Grid,
	Paper,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	styled,
	Checkbox,
	TextField,
	Tooltip,
} from "@mui/material";
import { Snackbar, Alert } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

const ResultGeneration = () => {
	const [isLoading, setIsLoading] = useState(true);
	const [showUserGuide, setShowUserGuide] = useState(false);
	const [sessions, setSessions] = useState([]);
	const [examTypes, setExamTypes] = useState([]);
	const [session, setSession] = useState("");
	const [examType, setExamType] = useState("");
	const [className, setClassName] = useState("");
	const [classes, setClasses] = useState([]);
	const [section, setSection] = useState("");
	const [sections, setSections] = useState([]);
	const [resultData, setResultData] = useState([]);
	const [remarks, setRemarks] = useState({});
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const [permissions, setPermissions] = useState({
		uRead: 0,
		uWrite: 0,
		uModify: 0,
		uDelete: 0,
		isPermission: 0,
	});
	const [formId, setFormId] = useState(null);
	const [error, setError] = useState(null);

	const token = sessionStorage.getItem("token");
	const apiUrl = process.env.REACT_APP_BASE_URL;

	const StyledButton = styled(Button)(({ theme }) => ({
		borderRadius: 20,
		padding: "8px 24px",
		textTransform: "none",
		fontWeight: 600,
		transition: "all 0.3s ease",
		"&:hover": {
			transform: "translateY(-2px)",
			boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
		},
	}));

	// Fetch Form ID
	const fetchFormId = async () => {
		try {
			const response = await fetch(
				`${apiUrl}/ExclusivePermission/Form_Authorizer`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: token,
					},
					body: JSON.stringify({ moduleId: 23 }),
				}
			);
			const data = await response.json();
			console.log("Form Authorizer Response:", data);
			if (response.ok) {
				const resultForm = data.find(
					(form) => form.formName === "Result Generation"
				);
				if (resultForm) {
					setFormId(resultForm.formId);
					console.log("Form ID set:", resultForm.formId);
				} else {
					setError("Result Generation form not found in response");
					setIsLoading(false);
				}
			} else {
				setError("Failed to fetch form ID");
				setSnackbar({
					open: true,
					message: "Failed to fetch form ID",
					severity: "error",
				});
				setIsLoading(false);
			}
		} catch (error) {
			console.error("Error fetching form ID:", error);
			setError("Error fetching form ID");
			setSnackbar({
				open: true,
				message: "Error fetching form ID",
				severity: "error",
			});
			setIsLoading(false);
		}
	};

	// Fetch Permissions
	const fetchPermissions = async () => {
		if (!formId) return;
		try {
			const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: token,
				},
				body: JSON.stringify({ formId }),
			});
			const data = await response.json();
			console.log("Module Authorizer Response:", data);
			if (response.ok && data.length > 0) {
				setPermissions(data[0]);
				console.log("Permissions set:", data[0]);
			} else {
				setPermissions({ ...permissions, isPermission: 0 });
				setError("Failed to fetch permissions");
				setSnackbar({
					open: true,
					message: "Failed to fetch permissions",
					severity: "error",
				});
			}
		} catch (error) {
			console.error("Error fetching permissions:", error);
			setError("Error fetching permissions");
			setSnackbar({
				open: true,
				message: "Error fetching permissions",
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleRemarkChange = (id, value) => {
		setRemarks((prevRemarks) => ({
			...prevRemarks,
			[id]: value,
		}));
	};

	// Initialize default remarks when result data changes
	useEffect(() => {
		const initialRemarks = {};
		resultData.forEach((student) => {
			initialRemarks[student.studentId] = "";
		});
		setRemarks(initialRemarks);
	}, [resultData]);

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	const fetchSessions = async () => {
		try {
			const response = await fetch(
				`${apiUrl}/ClassPromotion/GetFinancialYear`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: token,
					},
				}
			);

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();

			if (Array.isArray(data)) {
				setSessions(data);

				const today = new Date();
				const currentYear = today.getFullYear();
				const nextYear = currentYear + 1;

				const financialYearStart = new Date(currentYear, 3, 1);
				const financialYearEnd = new Date(nextYear, 2, 31);

				let currentSessionString;
				if (today >= financialYearStart && today <= financialYearEnd) {
					currentSessionString = `${currentYear}-${nextYear}`;
				} else {
					currentSessionString = `${currentYear - 1}-${currentYear}`;
				}

				const currentSession = data.find(
					(item) => item.finanacialYear === currentSessionString
				);

				if (currentSession) {
					setSession(currentSession.financialYearID);
				}
			}
		} catch (error) {
			console.error("Error fetching financial years:", error);
		}
	};

	const fetchExamTypes = async () => {
		try {
			const response = await fetch(`${apiUrl}/Exam/ExamList`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: token,
				},
				body: JSON.stringify({}),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();

			if (Array.isArray(data)) {
				setExamTypes(
					data.map((item) => ({
						id: item.examTypeId,
						name: item.examType,
					}))
				);
			}
		} catch (error) {
			console.error("Error fetching exam types:", error);
		}
	};

	const fetchClasses = async () => {
		try {
			const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: token,
				},
				body: JSON.stringify({}),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();

			if (Array.isArray(data)) {
				setClasses(
					data.map((item) => ({
						id: item.classId,
						name: item.className,
					}))
				);
			}
		} catch (error) {
			console.error("Error fetching class names:", error);
		}
	};

	const fetchSections = async (classId) => {
		try {
			const response = await fetch(`${apiUrl}/Exam/ddlSection_clsId`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: token,
				},
				body: JSON.stringify({ classId }),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();

			if (Array.isArray(data)) {
				setSections(
					data.map((item) => ({
						id: item.sectionId,
						name: item.sectionName,
					}))
				);
			}
		} catch (error) {
			console.error("Error fetching sections:", error);
		}
	};

	const handleClassChange = (event) => {
		const selectedClass = event.target.value;
		setClassName(selectedClass);
		setResultData([]);
		if (selectedClass) {
			fetchSections(selectedClass);
		} else {
			setSections([]);
		}
	};

	const handleGetResult = async () => {
		if (!session || !className || !section) {
			setSnackbar({
				open: true,
				message: "Please select all fields!",
				severity: "error",
			});
			return;
		}

		try {
			setIsLoading(true);

			const response = await fetch(
				"https://wsmsv1.aidigitalsec.com/api/Result/GetStudentList_ResultGeneration",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: token,
					},
					body: JSON.stringify({
						sessionId: session,
						examTypeId: 0,
						classId: className,
						sectionId: section,
					}),
				}
			);

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();

			if (Array.isArray(data)) {
				setResultData(data);
				console.log("Fetched Result Data:", data);
			} else {
				console.error("Unexpected API response format");
				setResultData([]);
			}
		} catch (error) {
			console.error("Error fetching result data:", error);
			setSnackbar({
				open: true,
				message: "Error fetching result data.",
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleSave = async () => {
		try {
			if (!session || !className || !section) {
				setSnackbar({
					open: true,
					message: "Please select all fields!",
					severity: "error",
				});
				return;
			}

			const payload = resultData
				.filter((student) => remarks[student.studentId])
				.map((student) => ({
					studentId: student.studentId,
					classTeacherId: sessionStorage.getItem("employeeId"),
					sessionId: session,
					examTypeId: 0,
					teacher_Remarks: remarks[student.studentId],
				}));

			if (payload.length === 0) {
				setSnackbar({
					open: true,
					message: "No students selected for remarks!",
					severity: "error",
				});
				return;
			}

			const response = await fetch(
				"https://wsmsv1.aidigitalsec.com/api/Result/ResultGeneration",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: token,
					},
					body: JSON.stringify(payload),
				}
			);

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			setSnackbar({
				open: true,
				message: "Data saved successfully!",
				severity: "success",
			});
		} catch (error) {
			console.error("Error saving data:", error);
			setSnackbar({
				open: true,
				message: "Failed to save data.",
				severity: "error",
			});
		}
	};

	const handleCancel = () => {
		setRemarks({});
	};

	const handleSessionChange = (event) => {
		setSession(event.target.value);
		setResultData([]);
	};

	const handleExamTypeChange = (event) => {
		setExamType(event.target.value);
		setResultData([]);
	};

	const handleSectionChange = (event) => {
		setSection(event.target.value);
		setResultData([]);
	};

	useEffect(() => {
		fetchFormId();
	}, []);

	useEffect(() => {
		if (formId) {
			fetchPermissions();
		}
	}, [formId]);

	useEffect(() => {
		if (permissions.isPermission && permissions.uRead) {
			fetchSessions();
			fetchExamTypes();
			fetchClasses();
		}
	}, [permissions]);

	if (isLoading) {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100vh",
				}}
			>
				<CircularProgress />
			</div>
		);
	}

	if (error || !permissions.isPermission) {
		return (
			<div style={{ padding: "20px", textAlign: "center" }}>
				<Typography variant="h4" color="error">
					You are not authorised to view this page
				</Typography>
			</div>
		);
	}

	return (
		<>
			<Box
				sx={{
					backgroundColor: "#0B1F40",
					padding: "12px 24px",
					boxShadow: 3,
					borderRadius: "4px 4px 0 0",
					margin: "2px auto",
					maxWidth: "900px",
				}}
			>
				<Typography
					variant="h4"
					sx={{ color: "#fff", textAlign: "center", fontWeight: "bold" }}
				>
					Result Generation{" "}
					<InfoIcon
						onClick={() => setShowUserGuide(true)}
						style={{ opacity: "0.6" }}
					/>
				</Typography>
			</Box>

			<Dialog
				open={showUserGuide}
				onClose={() => setShowUserGuide(false)}
				maxWidth="sm"
				fullWidth
				sx={{
					"& .MuiDialog-paper": {
						borderRadius: "12px",
						boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
						backgroundColor: "#f9fafb",
					},
				}}
			>
				<DialogTitle
					sx={{
						background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)",
						color: "white",
						fontWeight: "bold",
						textAlign: "center",
						borderRadius: "12px 12px 0 0",
						py: 2,
					}}
				>
					Result User Guide
				</DialogTitle>
				<DialogContent sx={{ padding: "24px" }}>
					<Typography
						variant="h6"
						sx={{
							color: "#1898c2",
							fontWeight: "600",
							mb: 2,
							mt: 1,
							textAlign: "center",
						}}
					>
						Quick Steps to Generate Result
					</Typography>
					<Box
						component="ul"
						sx={{
							listStyleType: "none",
							padding: 0,
							"& li": {
								display: "flex",
								alignItems: "flex-start",
								mb: 1.5,
								fontSize: "0.9rem",
								color: "#333",
							},
							"& li::before": {
								content: '"\\2022"',
								color: "#1898c2",
								fontWeight: "bold",
								display: "inline-block",
								width: "1.5em",
								mr: 1,
								fontSize: "1.2rem",
							},
							"& strong": {
								margin: "0 0.2em",
							},
						}}
					>
						<li>
							Select a <strong>Session</strong> from the dropdown menu.
						</li>
						<li>
							Choose an <strong>Exam Type</strong> from the dropdown menu.
						</li>
						<li>
							Select a <strong>Class</strong> from the dropdown menu.
						</li>
						<li>
							Select a <strong>Section</strong> from the dropdown menu.
						</li>
						<li>
							Click the <strong>Get Details</strong> button to retrieve the list
							of students and their results.
						</li>
						<li>
							Check the checkboxes next to the students' names to select them
							for adding remarks.
						</li>
						<li>
							Enter <strong>Remarks</strong> for the selected students in the
							provided text fields.
						</li>
						<li>
							Click the <strong>Save</strong> button to save the remarks for the
							selected students.
						</li>
						<li>
							If you need to clear the remarks, click the{" "}
							<strong>Cancel</strong> button.
						</li>
					</Box>
				</DialogContent>
				<DialogActions sx={{ justifyContent: "center", pb: 3 }}>
					<StyledButton
						onClick={() => setShowUserGuide(false)}
						color="primary"
						variant="contained"
						sx={{
							backgroundColor: "#1898c2",
							"&:hover": { backgroundColor: "#156f9a" },
							borderRadius: "8px",
							px: 4,
						}}
					>
						Close
					</StyledButton>
				</DialogActions>
			</Dialog>

			<Paper
				elevation={3}
				style={{ padding: "20px", margin: "10px auto", maxWidth: "900px" }}
			>
				<Grid container spacing={2} justifyContent="center" sx={{ mb: 2 }}>
					<Grid item xs={6}>
						<FormControl fullWidth>
							<Select
								value={session}
								onChange={handleSessionChange}
								displayEmpty
								sx={{ height: 45 }}
							>
								<MenuItem value="" disabled>
									Select Session
								</MenuItem>
								{sessions.map((item) => (
									<MenuItem
										key={item.financialYearID}
										value={item.financialYearID}
									>
										{item.finanacialYear}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={6}>
						{/* <FormControl fullWidth>
              <Select
                value={examType}
                onChange={handleExamTypeChange}
                displayEmpty
                sx={{ height: 45 }}
              >
                <MenuItem value="" disabled>
                  Select Exam Type
                </MenuItem>
                {examTypes.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
					</Grid>
				</Grid>

				<Grid container spacing={2} justifyContent="center" sx={{ mb: 3 }}>
					<Grid item xs={6}>
						<FormControl fullWidth>
							<Select
								value={className}
								onChange={handleClassChange}
								displayEmpty
								sx={{ height: 45 }}
							>
								<MenuItem value="" disabled>
									Select Class
								</MenuItem>
								{classes.map((item) => (
									<MenuItem key={item.id} value={item.id}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={6}>
						<FormControl fullWidth>
							<Select
								value={section}
								onChange={handleSectionChange}
								displayEmpty
								sx={{ height: 45 }}
								disabled={!className}
							>
								<MenuItem value="" disabled>
									Select Section
								</MenuItem>
								{sections.map((item) => (
									<MenuItem key={item.id} value={item.id}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
				</Grid>

				<Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
					<Button
						variant="contained"
						onClick={handleGetResult}
						sx={{ width: "200px", height: 45 }}
					>
						Get Details
					</Button>
				</Box>
				<Box sx={{ mt: 3 }}>
    {permissions.uRead ? (
        isLoading ? (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="150px"
            >
                <CircularProgress color="primary" />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Fetching result data...
                </Typography>
            </Box>
        ) : resultData.length > 0 ? (
            <Box>
                <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
                    Result Data
                </Typography>
                <Table sx={{ border: "1px solid #ccc" }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableCell align="center">
                                <Checkbox
                                    checked={
                                        resultData.length > 0 &&
                                        resultData.every(
                                            (student) => remarks[student.studentId] !== undefined
                                        )
                                    }
                                    indeterminate={
                                        resultData.some(
                                            (student) => remarks[student.studentId] !== undefined
                                        ) &&
                                        !resultData.every(
                                            (student) => remarks[student.studentId] !== undefined
                                        )
                                    }
                                    onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        const newRemarks = {};

                                        if (isChecked) {
                                            // Select all
                                            resultData.forEach((student) => {
                                                newRemarks[student.studentId] =
                                                    remarks[student.studentId] || "";
                                            });
                                        } else {
                                            // Deselect all
                                            resultData.forEach((student) => {
                                                newRemarks[student.studentId] = undefined;
                                            });
                                        }
                                        setRemarks(newRemarks);
                                    }}
                                />
                            </TableCell>
                            <TableCell align="center">Roll No</TableCell>
                            <TableCell align="center">Student Name</TableCell>
                            <TableCell align="center">Total Marks</TableCell>
                            <TableCell align="center">Obtained Marks</TableCell>
                            <TableCell align="center">Grade</TableCell>
                            <TableCell align="center">Remark</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {resultData.map((student, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                                    "&:nth-of-type(even)": { backgroundColor: "#ffffff" },
                                }}
                            >
                                <TableCell align="center">
                                    <Checkbox
                                        checked={remarks[student.studentId] !== undefined}
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            handleRemarkChange(
                                                student.studentId,
                                                isChecked
                                                    ? remarks[student.studentId] || ""
                                                    : undefined
                                            );
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="center">{student.rollNo}</TableCell>
                                <TableCell align="center">
                                    {student.studentName}
                                </TableCell>
                                <TableCell align="center">
                                    {student.totalMaxMarks}
                                </TableCell>
                                <TableCell align="center">
                                    {student.totalObtainedMarks}
                                </TableCell>
                                <TableCell align="center">{student.grade}</TableCell>
                                <TableCell align="center">
                                    <TextField
                                        size="small"
                                        placeholder="Enter Remark"
                                        variant="outlined"
                                        fullWidth
                                        onChange={(e) =>
                                            handleRemarkChange(
                                                student.studentId,
                                                e.target.value
                                            )
                                        }
                                        value={remarks[student.studentId] || ""}
                                        disabled={remarks[student.studentId] === undefined}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Tooltip
                        title={
                            permissions.uWrite
                                ? ""
                                : "You are not authorised to save results"
                        }
                    >
                        <span>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{ mr: 2, width: "120px" }}
                                onClick={handleSave}
                                disabled={!permissions.uWrite}
                            >
                                Save
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        color="secondary"
                        sx={{ width: "120px" }}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Box>
            </Box>
        ) : (
            <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>
                No data to display.
            </Typography>
        )
    ) : (
        <Typography variant="h6" sx={{ textAlign: "center", mt: 2 }}>
            You are not authorised to view results
        </Typography>
    )}
</Box>

				<Snackbar
					open={snackbar.open}
					autoHideDuration={6000}
					onClose={handleCloseSnackbar}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<Alert
						onClose={handleCloseSnackbar}
						severity={snackbar.severity}
						sx={{ width: "100%" }}
					>
						{snackbar.message}
					</Alert>
				</Snackbar>
			</Paper>
		</>
	);
};

export default ResultGeneration;
